import React, { useEffect, useState } from 'react';
import { Monitor, Smartphone, ShieldCheck, Trash2, RefreshCw, AlertCircle, CheckCircle, SmartphoneNfc } from 'lucide-react';
import { fetchUserDevices, deleteUserDevice } from '../services/api';

interface DeviceItem {
  id: string;
  device_name: string;
  device_type: string;
  os_info?: string;
  browser_info?: string;
  ip_address?: string;
  registered_at: string;
  last_active_at: string;
  isCurrentDevice: boolean;
}

interface DeviceManagementProps {
  userProfile?: any;
}

const TIER_DEVICE_LIMITS: Record<string, number> = {
  FREE: 1,
  TIER1: 1,
  TIER2: 2,
  TIER3: 3,
  TIER4: 5,
  TIER5: 10
};

const getLimit = (t?: string) => {
  if (!t) return 1;
  return TIER_DEVICE_LIMITS[t.toUpperCase()] ?? 1;
};

export const DeviceManagement: React.FC<DeviceManagementProps> = ({ userProfile }) => {
  const profileTier = userProfile?.membershipTier || userProfile?.membership_tier || 'TIER1';
  const [loading, setLoading] = useState<boolean>(true);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [tier, setTier] = useState<string>(profileTier);
  const [maxAllowed, setMaxAllowed] = useState<number>(getLimit(profileTier));
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const currentProfileTier = userProfile?.membershipTier || userProfile?.membership_tier;
    if (currentProfileTier) {
      setTier(currentProfileTier);
      setMaxAllowed(getLimit(currentProfileTier));
    }
  }, [userProfile?.membershipTier, userProfile?.membership_tier]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      setMsg(null);
      const res = await fetchUserDevices();
      if (res) {
        setDevices(res.devices || []);
        if (res.membershipTier) setTier(res.membershipTier);
        if (res.maxDevicesAllowed) setMaxAllowed(res.maxDevicesAllowed);
      }
    } catch (err: any) {
      console.error('[LoadDevices Error]', err);
      setMsg({ type: 'error', text: '등록 디바이스 목록을 불러오는 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [userProfile?.membershipTier, userProfile?.membership_tier]);

  const handleDelete = async (deviceId: string, isCurrent: boolean) => {
    if (isCurrent) {
      if (!confirm('현재 접속 중인 기기의 등록을 해지하시겠습니까? 해지 후 재로그인이 필요할 수 있습니다.')) return;
    } else {
      if (!confirm('이 기기의 등록을 해지하시겠습니까?')) return;
    }

    try {
      setDeletingId(deviceId);
      setMsg(null);
      await deleteUserDevice(deviceId);
      setMsg({ type: 'success', text: '디바이스 등록이 해지되었습니다.' });
      await loadDevices();
    } catch (err: any) {
      console.error('[DeleteDevice Error]', err);
      setMsg({ type: 'error', text: err.message || '디바이스 해지 실패' });
    } finally {
      setDeletingId(null);
    }
  };

  const getDeviceIcon = (type?: string) => {
    if (type === 'mobile' || type === 'tablet') {
      return <Smartphone className="w-5 h-5 text-indigo-400" />;
    }
    return <Monitor className="w-5 h-5 text-emerald-400" />;
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div className="p-5 bg-gradient-to-r from-slate-900 via-slate-850 to-indigo-950/40 border border-slate-800 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl">
            <SmartphoneNfc className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-white">이용 단말 관리</h3>
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-full">
                {tier}
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              현재 멤버십({tier}) 등급 기준 최대 <span className="font-semibold text-white">{maxAllowed}대</span>까지 등록 가능합니다.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs font-semibold text-slate-400">등록 단말</div>
            <div className="text-lg font-black text-white font-mono">
              {devices.length} <span className="text-xs font-normal text-slate-500">/ {maxAllowed}대</span>
            </div>
          </div>
          <button
            onClick={loadDevices}
            disabled={loading}
            className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors disabled:opacity-50"
            title="새로고침"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {msg && (
        <div className={`p-4 rounded-2xl text-xs font-medium flex items-center gap-2 ${msg.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
          }`}>
          {msg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Device List */}
      <div className="space-y-3">
        {loading ? (
          <div className="p-8 text-center text-xs text-slate-500 italic bg-slate-900/40 border border-slate-800/80 rounded-2xl">
            디바이스 목록을 불러오는 중...
          </div>
        ) : devices.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-500 italic bg-slate-900/40 border border-slate-800/80 rounded-2xl">
            등록된 기기가 없습니다.
          </div>
        ) : (
          devices.map((device) => (
            <div
              key={device.id}
              className={`p-5 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${device.isCurrentDevice
                  ? 'bg-indigo-950/20 border-indigo-500/30 shadow-md'
                  : 'bg-slate-900/60 hover:bg-slate-900 border-slate-800'
                }`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-800 border border-slate-700/60 rounded-2xl mt-0.5">
                  {getDeviceIcon(device.device_type)}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-bold text-white">{device.device_name}</h4>
                    {device.isCurrentDevice && (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" />
                        현재 디바이스
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 font-mono">
                    {[device.os_info, device.browser_info].filter(Boolean).join(' • ') || '정보 없음'}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono pt-1">
                    <span>IP: {device.ip_address || '내부 IP'}</span>
                    <span>•</span>
                    <span>마지막 활동: {formatDate(device.last_active_at)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <button
                  onClick={() => handleDelete(device.id, device.isCurrentDevice)}
                  disabled={deletingId === device.id}
                  className="px-3.5 py-2 text-xs font-semibold bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {deletingId === device.id ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  등록 해지
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
